import { useEffect, useRef, useState } from "react";
import type { MouseEvent, ReactElement, RefObject } from "react";
import type { Unternehmen } from "@kundeninfo/api-client";
import { Icon, useSizeClass, contrastRatio, umgebungsKennung, useEscapeEbene,
         useMenuTasten, MeKopfleiste } from "@kundeninfo/ui";
import { Auswahl } from "@kundeninfo/ui/me-design";
import type { UmgebungsAnzeige } from "@kundeninfo/ui";
import type { Benutzer } from "../session.js";
import { logoQuelle } from "../logoQuelle.js";
import { bereicheFuer, einstellungsEintraege, istAktiv, zielPfad } from "./Navigation.js";
import { anwendungsname } from "../zuschnitt.js";
import type { Zuschnitt } from "../zuschnitt.js";
import type { Eintrag, NavigationsZiel } from "./Navigation.js";
import "./Kopfzeile.css";

export type KopfzeileProps = {
  user: Benutzer;
  firma: Unternehmen | null;
  onLogout: () => void;
  onOpenSettings: () => void;
  /** Der Navigationspunkt, auf dem die Anwendung gerade steht. */
  aktiv: NavigationsZiel;
  /**
   * Die ADRESSE, auf der die Anwendung gerade steht — als Zeichenkette.
   *
   * Sie steht neben `aktiv` und ist nicht dasselbe: `aktiv` sagt, welcher Punkt
   * hervorgehoben wird, und bleibt über einen ganzen Bereich hinweg gleich. Ein offenes
   * Menü darf aber schon einen Bildschirmwechsel INNERHALB des Bereichs nicht überleben
   * (Fixrunde 1): Wer mit Alt+← von `/kunden/1` zurück auf `/kunden` geht, hat den
   * Bildschirm gewechselt, ohne dass sich der hervorgehobene Punkt geändert hätte — und
   * fand sein Menü bis dahin noch offen vor.
   */
  routenschluessel: string;
  /**
   * Zeigt „Verwaltung" im Zahnrad-Menü. Nur für ADMIN — der Server weist alle
   * Verwaltungsrouten für alle anderen mit 403 ab (fail closed), diese Prüfung sorgt nur
   * dafür, dass die Oberfläche niemanden in eine sichere 403 hineinführt.
   */
  istAdmin: boolean;
  onNavigate: (ziel: NavigationsZiel) => void;
  /**
   * Die WELT dieser Instanz (Spec „Umgebungskennzeichnung"). Der Rahmen färbt daraus die
   * Kopfleiste (packages/ui MeSchale), HIER steht ihr sichtbarer Text: die Kennung
   * „TEST · live05" als erstes Element der rechten Gruppe. Bei Produktion steht dort
   * nichts — Produktion ist der stille Zustand.
   */
  umgebung: UmgebungsAnzeige;
  /**
   * Die sichtbaren Unternehmen (Sitzung) — die Einträge des ARBEITSUNTERNEHMEN-Wechslers
   * (ME-Etappe 1, Aufgabe 6). Gewählt ist `firma`, das Arbeitsunternehmen. Fehlt dies oder
   * `onArbeitsunternehmen`, steht kein Wechsler in der Leiste.
   */
  unternehmen?: Unternehmen[];
  /** Meldet die Wahl im Wechsler (die Kennung des Unternehmens). */
  onArbeitsunternehmen?: (id: string) => void;
  /** Der Zuschnitt (Spec „Belegdesign als eigenständige ME-Anwendung“): Namenszug und
   *  Bereiche. Vorgabe `voll`. Kosmetik, kein Sicherheitsmerkmal (siehe zuschnitt.ts). */
  zuschnitt?: Zuschnitt;
};

/**
 * Die Wortmarke — eigenes Bauteil, weil sie im RAHMEN steckt und nicht in der Kopfzeile:
 * `MeSchale` zeichnet sie als erstes Kind der Kopfleiste, noch vor allem, was ein Ziel
 * beisteuert (siehe den `wortmarke`-Steckplatz dort). Sie bleibt trotzdem in dieser Datei,
 * weil sie sich mit der Kopfzeile die Stilvorlage teilt und beide dieselbe Frage
 * beantworten müssen: Was steht da, wenn es kein Heimatunternehmen gibt?
 */
export type WortmarkeProps = {
  firma: Unternehmen | null;
  umgebung: UmgebungsAnzeige;
  /** Der Weg zur Startseite, den die Wortmarke geht. */
  onHome: () => void;
  /** Im Zuschnitt „belegdesign" heisst die Anwendung so, und die Startseite ist der
   *  Belegdesigner. Vorgabe `voll`. */
  zuschnitt?: Zuschnitt;
};

/** Die Adresse der Startseite — dieselbe, die `routeToPath({ name: "kundenliste" })`
 *  liefert. Als echtes `href`, damit Mittelklick und „in neuem Reiter öffnen" gehen. */
const STARTSEITE = "/kunden";

function initialen(name: string): string {
  const teile = name.trim().split(/\s+/);
  const erste = teile[0]?.[0] ?? "";
  const letzte = teile.length > 1 ? (teile[teile.length - 1]?.[0] ?? "") : "";
  return (erste + letzte).toUpperCase() || "?";
}

/**
 * Bleibt DIESER Klick auf einem Link in der Anwendung?
 *
 * Mittelklick, Cmd/Strg-Klick und „in neuem Reiter öffnen" bleiben dem BROWSER: Sie sollen
 * die echte Adresse aus dem `href` öffnen, und genau dafür ist sie da. Nur der schlichte
 * Linksklick wird abgefangen. Die Regel steht einmal hier, weil jeder Link der Kopfleiste
 * sie braucht — die Wortmarke, die Bereiche und die Einträge des Zahnrad-Menüs.
 */
function nurEinfacherKlick(ereignis: MouseEvent<HTMLAnchorElement>): boolean {
  if (ereignis.defaultPrevented || ereignis.button !== 0) return false;
  if (ereignis.metaKey || ereignis.ctrlKey || ereignis.shiftKey || ereignis.altKey) return false;
  return true;
}

/**
 * Das Markenzeichen des Heimatunternehmens. Woher die beiden Bilder kommen, entscheidet
 * `logoQuelle` je Variante: ein HOCHGELADENES Logo geht der statischen Datei vor (Spec:
 * „hochgeladen vor Datei"), der Rückfall bleibt der Bestand aus `/logos/`.
 */
function Markenzeichen({ firma, variante }: { firma: Unternehmen; variante: "wortmarke" | "bildmarke" }): ReactElement {
  const hell = logoQuelle(firma, variante);
  const dunkel = logoQuelle(firma, `${variante}-dunkel`);

  // Weder hochgeladen noch hinterlegt: Kürzel auf der Markenfarbe als Fläche. Das gilt
  // auch dann, wenn nur die DUNKLE Fassung da ist — auf hellem Grund bliebe der Platz
  // sonst leer, und die Kopfzeile braucht in jeder Welt ein Zeichen.
  // Kein Chip (P16, Entscheid 41 geprüft): das Abzeichen ist der Platzhalter für die
  // Bildmarke — VOLL in der Markenfarbe auf der Kopfleiste, keine 14-%-Tönung wie
  // `Chip farbe`; es bleibt deshalb ein eigenes Zeichen und ist kein Übergang mehr.
  if (hell === null) {
    const textHell = contrastRatio(firma.colorHex, "#ffffff") >= 4.5 ? "#ffffff" : "#111111";
    return (
      <span className="kz__abzeichen" style={{ background: firma.colorHex, color: textHell }}>
        {firma.shortCode}
      </span>
    );
  }

  const hatDunkel = dunkel !== null;
  return (
    <span className={`kz__marke-bild kz__marke-bild--${variante}${hatDunkel ? "" : " kz__marke-bild--nur-hell"}`}>
      <img src={hell} alt={firma.name} className="kz__marke-hell" />
      {hatDunkel && <img src={dunkel} alt={firma.name} className="kz__marke-dunkel" />}
    </span>
  );
}

/**
 * Die Wortmarke des Heimatunternehmens als Weg auf die Startseite — das erste Element der
 * Kopfleiste, in jeder Grössenklasse und in jedem Fall (Hausregel, Spec
 * „Umgebungskennzeichnung", Dirk 07.09.2026).
 *
 * Auf der HELLEN Testwelt läuft sie einfarbig dunkel, auf den beiden dunklen Welten in
 * ihrer aufgehellten Fassung — deshalb braucht sie die Umgebung. Die Unterscheidung hing
 * bis zum 08.09.2026 am FARBSCHEMA; das war richtig, solange die Kopfleiste die
 * Flächenfarbe der Seite trug. Jetzt trägt sie die Farbe ihrer Welt, und die ist
 * theme-unabhängig: Im Hellmodus auf einem Produktivsystem steht die Wortmarke auf
 * Dunkelgrün, und ein am Schema festgemachter Umschalter zeigte dort die dunkle
 * Logofassung auf dunklem Grund.
 */
export function Wortmarke(
  { firma, umgebung, onHome, zuschnitt = "voll" }: WortmarkeProps,
): ReactElement {
  const klasse = useSizeClass();
  const name = anwendungsname(zuschnitt);
  const startseite = zuschnitt === "belegdesign" ? zielPfad("belegdesign") : STARTSEITE;
  const dunklerGrund = umgebung.klasse !== "test";

  function zurStartseite(ereignis: MouseEvent<HTMLAnchorElement>): void {
    if (!nurEinfacherKlick(ereignis)) return;
    ereignis.preventDefault();
    onHome();
  }

  return (
    /*
      Ohne Heimatunternehmen trägt der Link den Namenszug der Anwendung statt eines Logos —
      ein Platzhalter mit dem Text "Kundeninfo" neben dem gleichlautenden Namenszug der
      Kopfzeile ergäbe "Kundeninfo Kundeninfo" (Betriebsbefund B1), und ein Link ohne
      sichtbaren Inhalt wäre kein Link.
    */
    <a className={`kz__heim${dunklerGrund ? " kz__heim--dunkler-grund" : ""}`}
       href={startseite} onClick={zurStartseite}
       aria-label={`${name}, zur Startseite`}>
      {firma !== null
        ? (
          <span className="kz__logo">
            <Markenzeichen firma={firma} variante={klasse === "phone" ? "bildmarke" : "wortmarke"} />
          </span>
        )
        : <span className="kz__marketname">{name}</span>}
    </a>
  );
}

/**
 * Die BEREICHSREIHE — die fünf Orte, an denen gearbeitet wird, offen in der Kopfleiste
 * (Entscheid Dirk 10.09.2026, Design-Gespräch). Sie ersetzt die linke Navigationsspalte,
 * die es auf iPad und Desktop seither nicht mehr gibt; der Inhalt bekommt ihre Breite.
 *
 * Echte Links mit `href` und nicht Knöpfe: Ein Bereich IST eine Adresse, und wer ihn mit
 * gedrückter Cmd-Taste anklickt, will ihn in einem neuen Reiter. Genau dieselbe
 * Entscheidung wie an der Wortmarke.
 *
 * `min-width: 0; overflow: hidden` steht an der Reihe in der Stilvorlage (nicht hier):
 * Läuft sie einmal über, soll sie sich selbst beschneiden und nicht die Kopfleiste
 * aufdrängen — die rechte Gruppe (Kennung, Werkzeuge) muss unter allen Umständen stehen
 * bleiben. Damit es dazu nicht kommt, gibt die Leiste zweimal vorher nach: Erst entfällt
 * der Namenszug, dann fallen die WÖRTER der Bereiche weg und nur die Icons bleiben (beides
 * in Kopfzeile.css, mit den gemessenen Zahlen).
 *
 * Auf dem TELEFON wird sie gar nicht erst gebaut — dort trägt die untere Leiste die
 * Bereiche, und eine zweite Kopfzeile darüber wäre eine Zeile zu viel.
 */
function Bereichsreihe(
  { aktiv, onNavigate, eintraege }: {
    aktiv: NavigationsZiel; onNavigate: (z: NavigationsZiel) => void; eintraege: Eintrag[];
  },
): ReactElement {
  return (
    /*
      „Hauptnavigation" und nicht „Bereiche" (Fixrunde 1): Auf iPad und Desktop IST diese
      Reihe die Hauptnavigation — die linke Spalte, die den Namen bis zum 10.09.2026 trug,
      gibt es nicht mehr. Eine Doppelung mit der unteren Leiste kann daraus nicht werden:
      Die gibt es nur auf dem Telefon, und dort wird diese Reihe gar nicht erst gebaut.
    */
    <nav className="kz__bereiche" aria-label="Hauptnavigation">
      {eintraege.map((e) => {
        const ziel = e.ziel;
        // Ein noch nicht gebautes Modul (`fertig: false`) oder eines ohne Ziel ist kein
        // Link: Es steht abgeblendet da, damit die Reihenfolge der Reihe von Anfang an
        // feststeht — dieselbe Regel wie in der unteren Leiste (siehe Navigation.tsx).
        if (ziel === undefined || !e.fertig) {
          return (
            <span key={e.id} className="kz__bereich kz__bereich--stumm"
                  title={`${e.label} — kommt in einem späteren Paket.`}>
              <Icon name={e.icon} size={20} />
              <span className="kz__bereich-wort">{e.label}</span>
            </span>
          );
        }
        const hier = istAktiv(e, aktiv);
        return (
          <a
            key={e.id}
            className={`kz__bereich${hier ? " kz__bereich--aktiv" : ""}`}
            href={zielPfad(ziel)}
            /*
              Name und Kurzhinweis stehen AM LINK und nicht nur im Wort daneben: Auf
              schmalen Fenstern zeigt die Reihe nur noch die Icons (siehe Kopfzeile.css),
              und ohne diese beiden wäre der Link dort für Bildschirmleser namenlos und für
              das Auge eine Rate-Aufgabe. Dass der Name doppelt im Dokument steht, stört
              nicht: Ein `aria-label` ERSETZT den Inhalt als zugänglichen Namen.
            */
            aria-label={e.label}
            title={e.label}
            aria-current={hier ? "page" : undefined}
            onClick={(ereignis) => {
              if (!nurEinfacherKlick(ereignis)) return;
              ereignis.preventDefault();
              onNavigate(ziel);
            }}
          >
            <Icon name={e.icon} size={20} />
            <span className="kz__bereich-wort">{e.label}</span>
          </a>
        );
      })}
    </nav>
  );
}

/**
 * Ein Eintrag im Zahnrad-Menü. Auch hier ein echter Link, aus demselben Grund wie in der
 * Bereichsreihe — und mit `role="menuitem"`, weil er in einem Menü steht.
 */
function Menuziel(
  p: {
    eintrag: Eintrag; aktiv: NavigationsZiel;
    onNavigate: (z: NavigationsZiel) => void; onFertig: () => void;
  },
): ReactElement | null {
  const ziel = p.eintrag.ziel;
  /*
   * Ein noch nicht gebautes Modul (`fertig: false`) oder eines ohne Ziel steht als
   * ABGEBLENDETER Eintrag da und nicht als Link — dieselbe Regel wie in der Bereichsreihe
   * und in der unteren Leiste: Die Reihenfolge und Vollständigkeit des Menüs soll von
   * Anfang an feststehen. `aria-disabled` statt `disabled`, weil ein Link das Attribut
   * nicht kennt; das Tastaturmuster (useMenuTasten) überspringt ihn daran.
   */
  if (ziel === undefined || !p.eintrag.fertig) {
    return (
      <span role="menuitem" aria-disabled="true"
            className="kz__menu-eintrag kz__menu-eintrag--stumm"
            title={`${p.eintrag.label} — kommt in einem späteren Paket.`}>
        <Icon name={p.eintrag.icon} size={16} />
        {p.eintrag.label}
      </span>
    );
  }
  const hier = istAktiv(p.eintrag, p.aktiv);
  return (
    <a
      role="menuitem"
      className={`kz__menu-eintrag${hier ? " kz__menu-eintrag--aktiv" : ""}`}
      href={zielPfad(ziel)}
      aria-current={hier ? "page" : undefined}
      onClick={(ereignis) => {
        if (!nurEinfacherKlick(ereignis)) return;
        ereignis.preventDefault();
        p.onFertig();
        p.onNavigate(ziel);
      }}
    >
      <Icon name={p.eintrag.icon} size={16} />
      {p.eintrag.label}
    </a>
  );
}

/**
 * Die Umgebungskennung, das erste Element der rechten Gruppe.
 *
 * Sie steht als ZWEI Fassungen im Dokument (lang und kurz) und nicht als eine, die das
 * Programm je nach Breite auswählt: Welche passt, ist eine Frage der Containerbreite, und
 * die kennt CSS zu jedem Zeitpunkt genauer als ein Zustand, der einem Neuzeichnen
 * hinterherläuft. Für Screenreader ist beides zusammen kein Doppel — die Ansage steht
 * ohnehin am Banner (MeSchale), die Kennung selbst ist hier `aria-hidden`.
 */
function Umgebungskennung({ umgebung }: { umgebung: UmgebungsAnzeige }): ReactElement | null {
  const teile = umgebungsKennung(umgebung);
  if (teile === null) return null;
  return (
    <span className="kz__umgebung" aria-hidden="true">
      <span className="kz__umgebung-welt">{teile.welt}</span>
      <span className="kz__umgebung-welt-kurz">{teile.weltKurz}</span>
      {teile.zusatz !== null && (
        <span className="kz__umgebung-zusatz"> · {teile.zusatz}</span>
      )}
    </span>
  );
}

/**
 * Kopfzeile: ganz links die Wortmarke des Heimatunternehmens (Bildmarke auf dem iPhone)
 * als Link auf die Startseite, dann der Namenszug der Anwendung; rechts das Persönliche
 * und Technische, angeführt von der Umgebungskennung.
 *
 * Die REIHENFOLGE ist eine Hausregel und keine Geschmacksfrage (Spec
 * „Umgebungskennzeichnung", Abschnitt „Aufbau der Kopfleiste", Dirk 07.09.2026): Links
 * steht, WO man ist und was es gibt; rechts, WER man ist und WOMIT man es einstellt. Die
 * Umgebungskennung sitzt zwischen beidem, unmittelbar links der Lupe.
 *
 * Kundeninfo hat von der rechten Fünfergruppe der Spec (Suche, Alarm, Einstellungen,
 * Konto, Mandant) nur zwei Glieder: die Suche und das Konto. Alarm und Mandantenumschalter
 * gibt es hier nicht, und die Einstellungen („Darstellung") hängen im Konto-Menü, wo sie
 * seit dem ersten Paket sitzen. Fehlende Glieder entfallen ersatzlos, die Reihenfolge der
 * übrigen bleibt — genau das sagt die Spec.
 */
/** Welches der beiden Menüs der Kopfleiste gerade offen ist. */
type MenuName = "werkzeug" | "konto";

/**
 * Das gemeinsame Verhalten der beiden Menüs in der Kopfleiste: Escape schliesst und gibt
 * den Fokus an seinen Knopf zurück, ein Klick daneben schliesst, und die Tastatur folgt
 * dem Muster, das `role="menu"` verspricht.
 *
 * EIN Haken für beide, und dadurch von selbst höchstens eines offen: Der Zustand ist der
 * NAME des offenen Menüs und nicht zwei Wahrheitswerte nebeneinander — zwei aufgeklappte
 * Menüs, die sich überlappen, kann es damit gar nicht geben.
 *
 * Escape läuft über den gemeinsamen ESCAPE-STAPEL (@kundeninfo/ui, useEscapeEbene) und
 * nicht über einen eigenen Horcher am Dokument. Der Grund steht dort ausführlich: Mit einem
 * eigenen Horcher schloss ein Escape auf dem iPad das Menü UND den überlagernden
 * Kontextrand darunter, weil beide dasselbe Ereignis am selben Ziel sahen.
 *
 * Das TASTATURMUSTER (Fokus beim Aufklappen, Pfeile mit Umlauf, Home/End, roving tabindex)
 * kommt ebenfalls aus dem gemeinsamen Baustein — siehe useMenuTasten. `role="menu"` ist ein
 * Versprechen, und es wird jetzt an allen drei Menüs dieser Anwendung eingelöst.
 *
 * Bewusst OHNE Fokusfalle: Ein Menü in der Kopfleiste ist kein Dialog (dieselbe Abgrenzung
 * wie beim Überlaufmenü der unteren Leiste und bei den überlagernden Rändern des Rahmens).
 * Der Horcher für den Klick daneben hängt am Dokument, damit er auch greift, wenn der Fokus
 * nirgendwo steht — der Normalfall nach einer Zeigergeste.
 */
function useMenuschliessung(
  offen: MenuName | null,
  setOffen: (m: MenuName | null) => void,
  huellen: Record<MenuName, RefObject<HTMLDivElement | null>>,
  knoepfe: Record<MenuName, RefObject<HTMLButtonElement | null>>,
  menues: Record<MenuName, RefObject<HTMLDivElement | null>>,
): void {
  useEscapeEbene(offen !== null, () => {
    if (offen === null) return;
    const knopf = knoepfe[offen];
    setOffen(null);
    knopf.current?.focus();
  });

  /*
   * Die Tabulatortaste schliesst OHNE Fokusrückgabe (siehe useMenuTasten) — anders als
   * Escape oben, das den Fokus an den Knopf zurückgibt. Tab heisst „ich gehe weiter",
   * Escape „ich wollte das doch nicht".
   */
  useMenuTasten(offen === "werkzeug", menues.werkzeug, knoepfe.werkzeug,
    () => setOffen(null));
  useMenuTasten(offen === "konto", menues.konto, knoepfe.konto, () => setOffen(null));

  useEffect(() => {
    if (offen === null) return undefined;
    const huelle = huellen[offen];
    function beiZeiger(ereignis: globalThis.MouseEvent): void {
      const rahmen = huelle.current;
      if (rahmen !== null && ereignis.target instanceof Node
        && rahmen.contains(ereignis.target)) return;
      setOffen(null);
    }
    document.addEventListener("mousedown", beiZeiger);
    return () => document.removeEventListener("mousedown", beiZeiger);
    // `huellen` ist bei jedem Zeichnen ein FRISCHES Hüllobjekt (die Refs darin bleiben
    // dieselben) und steht deshalb nicht in dieser Liste: Es würde den Horcher bei jedem
    // Neuzeichnen ab- und wieder anmelden, ohne dass sich etwas geändert hätte (dieselbe
    // Überlegung wie in useFocusTrap.ts). `setOffen` kommt aus useState und ist ohnehin
    // stabil; es fehlt hier nur, weil es nichts beitrüge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen]);
}

export function Kopfzeile(
  { user, firma, onLogout, onOpenSettings, umgebung, aktiv, routenschluessel, istAdmin,
    onNavigate, unternehmen, onArbeitsunternehmen, zuschnitt = "voll" }: KopfzeileProps,
): ReactElement {
  const klasse = useSizeClass();
  const [offen, setOffen] = useState<MenuName | null>(null);
  const huellen = {
    werkzeug: useRef<HTMLDivElement | null>(null),
    konto: useRef<HTMLDivElement | null>(null),
  };
  const knoepfe = {
    werkzeug: useRef<HTMLButtonElement | null>(null),
    konto: useRef<HTMLButtonElement | null>(null),
  };
  const menues = {
    werkzeug: useRef<HTMLDivElement | null>(null),
    konto: useRef<HTMLDivElement | null>(null),
  };
  useMenuschliessung(offen, setOffen, huellen, knoepfe, menues);

  /*
   * Kein Zustand überlebt einen Kontextwechsel (Hausregel). Ein Menüeintrag schliesst sein
   * Menü zwar selbst — aber die Zurück-Taste des Browsers, ein Sprung aus der Seite heraus
   * und die Wortmarke tun das nicht, und ein Menü, das über einem längst gewechselten
   * Bildschirm stehen bleibt, gehört dort nicht mehr hin.
   *
   * Verglichen wird die ADRESSE und nicht der hervorgehobene Punkt (Fixrunde 1): Der bleibt
   * über einen ganzen Bereich hinweg gleich, und ein Wechsel INNERHALB des Bereichs — mit
   * Alt+← von `/kunden/1` zurück auf `/kunden` — ist trotzdem ein Bildschirmwechsel. Bis
   * dahin blieb das Menü dort offen stehen.
   *
   * WÄHREND des Zeichnens und nicht in einem Effekt, und das ist hier keine Stilfrage:
   * Ein Effekt läuft auch beim ERSTEN Zeichnen. Läuft er dabei — wie unter Last — einer
   * Zeigergeste hinterher, schliesst er ein Menü, das gerade erst aufgegangen ist. Genau
   * daran war der Torlauf zweimal rot, an zwei verschiedenen Stellen und jedes Mal
   * unreproduzierbar, sobald man die Datei allein laufen liess. Der Vergleich mit dem
   * VORIGEN Wert kann das nicht: Beim ersten Zeichnen ist er derselbe, und dann passiert
   * nichts. (Das ist Reacts eigenes Muster für „Zustand an eine geänderte Eigenschaft
   * anpassen"; React zeichnet sofort neu, bevor irgendetwas sichtbar wird.)
   */
  const [vorigeAdresse, setVorigeAdresse] = useState(routenschluessel);
  if (vorigeAdresse !== routenschluessel) {
    setVorigeAdresse(routenschluessel);
    setOffen(null);
  }

  const werkzeugEintraege = einstellungsEintraege(istAdmin, zuschnitt);
  // Auf dem Telefon trägt die untere Leiste die Bereiche; eine zweite Reihe in der
  // Kopfleiste wäre dort eine Zeile zu viel (Entscheid Dirk 10.09.2026).
  const zeigtBereiche = klasse !== "phone";

  /*
   * Der Arbeitsunternehmen-Wechsler (ME-Etappe 1, Aufgabe 6) — `me-auswahl` aus me-design,
   * kein Nachbau. Eine Option je sichtbarem Unternehmen in der Reihenfolge der Sitzung,
   * beschriftet mit dem Kürzel; auch bei nur EINEM Unternehmen steht er da (eine Option),
   * damit die Leiste überall gleich aussieht. Die Beschriftung „Arbeitsunternehmen" ist
   * der Name des Feldes für Screenreader; `kompakt` lässt sie nur für die Vorlesestimme
   * stehen, und me-kopfzeile färbt das Feld im Slot `wechsler` selbst (me-design 9778a04).
   */
  const wechsler = unternehmen !== undefined && onArbeitsunternehmen !== undefined
    && unternehmen.length > 0
    ? (
      <Auswahl className="ki-kopf__wechsler" beschriftung="Arbeitsunternehmen" kompakt
        wert={firma?.id ?? ""} onGeaendert={(d) => onArbeitsunternehmen(d.wert)}>
        {unternehmen.map((u) => <option key={u.id} value={u.id}>{u.shortCode}</option>)}
      </Auswahl>
    )
    : undefined;

  /*
   * Der RAHMEN ist seit ME-Etappe 1 (Aufgabe 6) ein `me-kopfzeile` (MeKopfleiste aus
   * @kundeninfo/ui). Die Inhalte bleiben, sie verteilen sich nur auf dessen drei Slots:
   * links (`titel`) Namenszug und Bereiche, daneben (`wechsler`) das Arbeitsunternehmen,
   * rechts (`werkzeuge`) Kennung, Suche, Zahnrad und Konto. Grund und Schrift der Welt
   * setzt das Element; der Abstand nach rechts kommt aus seinem eigenen Layout (der
   * frühere Platzhalter `kz__spacer` entfällt).
   */
  const titel = (
    <div className="kz">
      {/*
        Die Wortmarke steht NICHT hier, sondern im Rahmen (MeSchale, Steckplatz
        `wortmarke`) — sie ist das erste Kind im Titel der Kopfleiste, noch vor dem
        Rückweg-Knopf des Telefons; der Rahmen reicht beides über `KopfRahmenKontext`
        herein (MeKopfleiste). Hier beginnt deshalb das, was die ANWENDUNG beisteuert.

        Ohne Heimatunternehmen trägt schon die Wortmarke den Namenszug; er stünde sonst
        zweimal da ("Kundeninfo Kundeninfo", Betriebsbefund B1). Deshalb hängen Trenner
        UND Namenszug an derselben Bedingung.
      */}
      {firma !== null && <span className="kz__trenner" aria-hidden="true" />}
      {firma !== null && <span className="kz__marketname">{anwendungsname(zuschnitt)}</span>}
      {zeigtBereiche && <Bereichsreihe aktiv={aktiv} onNavigate={onNavigate}
        eintraege={bereicheFuer(zuschnitt)} />}
    </div>
  );

  const werkzeuge = (
    <div className="kz kz--werkzeuge">
      <Umgebungskennung umgebung={umgebung} />
      {/* Übergang P12-1 (docs/ME-DESIGN.md): Werkzeugknopf der Kopfleiste in der Umgebungsfarbe
         — me-kopfzeile hat keine Werkzeug-/Symbolknöpfe, me-knopf zeichnet in den
         Aktionsfarben. */}
      <button
        type="button"
        className="kz__werkzeug kz__werkzeug--suche kz__tapflaeche"
        disabled
        aria-label="Suche"
        title="Die übergreifende Suche kommt mit einem späteren Paket."
      >
        <Icon name="magnifying-glass" size={22} />
      </button>
      {/*
        Das ZAHNRAD (Entscheid Dirk 10.09.2026): Verwaltung, Schnittstellen, Einstellungen.
        Es steht auf ALLEN Breiten in der Kopfleiste — auch auf dem Telefon, wo die untere
        Leiste nur die Bereiche trägt.

        Sein Menü hängt UNTER der Leiste und gehört damit wieder zur Seite, nicht zur Welt:
        Es trägt dieselbe Stilvorlage wie das Konto-Menü (`kz__menu`) und liest die
        gewöhnlichen Flächen- und Textfarben.
      */}
      <div className="kz__werkzeug-huelle" ref={huellen.werkzeug}>
        {/* Übergang P12-1 (docs/ME-DESIGN.md): Werkzeugknopf der Kopfleiste in der
           Umgebungsfarbe — me-kopfzeile hat keine Werkzeug-/Symbolknöpfe, me-knopf zeichnet in
           den Aktionsfarben. */}
        <button
          type="button"
          className="kz__werkzeug kz__werkzeug--zahnrad kz__tapflaeche"
          ref={knoepfe.werkzeug}
          aria-haspopup="menu"
          aria-expanded={offen === "werkzeug"}
          /*
            „Anwendung einrichten" und nicht „Einstellungen" (Fixrunde 1): Ein Eintrag IN
            diesem Menü heisst „Einstellungen", und ein Knopf, der genauso heisst wie eines
            seiner Ziele, sagt einem Bildschirmleser zweimal dasselbe Wort und meint zweimal
            etwas anderes. Der Knopf öffnet, WORIN man die Anwendung einrichtet; einer der
            drei Wege darin sind die persönlichen Einstellungen.
          */
          aria-label="Anwendung einrichten"
          title="Anwendung einrichten"
          onClick={() => setOffen((jetzt) => (jetzt === "werkzeug" ? null : "werkzeug"))}
        >
          <Icon name="gear" size={22} />
        </button>
        {offen === "werkzeug" && (
          <div role="menu" className="kz__menu" aria-label="Anwendung einrichten"
               ref={menues.werkzeug}>
            {werkzeugEintraege.map((e) => (
              <Menuziel key={e.id} eintrag={e} aktiv={aktiv}
                onNavigate={onNavigate} onFertig={() => setOffen(null)} />
            ))}
          </div>
        )}
      </div>
      <div className="kz__benutzer" ref={huellen.konto}>
        {/* Übergang P12-1 (docs/ME-DESIGN.md): Werkzeugknopf der Kopfleiste in der
           Umgebungsfarbe — me-kopfzeile hat keine Werkzeug-/Symbolknöpfe, me-knopf zeichnet in
           den Aktionsfarben. */}
        <button
          type="button"
          className="kz__benutzer-knopf kz__tapflaeche"
          ref={knoepfe.konto}
          aria-haspopup="menu"
          aria-expanded={offen === "konto"}
          aria-label={`Benutzermenü, angemeldet als ${user.name}`}
          onClick={() => setOffen((jetzt) => (jetzt === "konto" ? null : "konto"))}
        >
          {initialen(user.name ?? "")}
        </button>
        {offen === "konto" && (
          <div role="menu" className="kz__menu" ref={menues.konto}>
            <p className="kz__menu-name">{user.name}</p>
            <p className="kz__menu-mail">{user.email}</p>
            {/* Übergang P12-1 (docs/ME-DESIGN.md): Kontomenü mit nativen Menüeinträgen —
               me-design hat kein Menü/keinen Menüeintrag (Entscheid 44). */}
            <button
              type="button"
              role="menuitem"
              className="kz__menu-eintrag"
              onClick={() => { setOffen(null); onOpenSettings(); }}
            >
              <Icon name="sliders" size={16} />
              Darstellung
            </button>
            <button type="button" role="menuitem" className="kz__menu-eintrag" onClick={onLogout}>
              <Icon name="lock" size={16} />
              Abmelden
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <MeKopfleiste umgebung={umgebung} titel={titel} wechsler={wechsler}
      werkzeuge={werkzeuge} />
  );
}
